(ns practitest.frontend.forms3.run.timer
  (:require
    [clojure.string :as str]
    [practitest.frontend.common.format-utils :as fu]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.tooltip :as tooltip]
    [practitest.frontend.forms3.run.events :as run-events]
    [practitest.frontend.forms3.run.subs :as run-subs]
    [re-frame.core :as re-frame]
    [reagent.core :as r]))

(def ^:private ms-per-second 1000)
(def ^:private ms-per-minute 60000)
(def ^:private seconds-per-minute 60)
(def ^:private seconds-per-hour 3600)

(defn- timer [{:keys [running? elapsed on-start on-stop on-cancel on-change idle-minutes]}]
  (r/with-let [handle             (atom nil)
               local-elapsed      (r/atom 0)
               editing?           (r/atom false)
               draft              (r/atom "")
               stopped-by-button? (atom false)
               paused-by-idle?    (r/atom false)
               last-activity      (atom (js/Date.now))
               idle-minutes-ref   (atom idle-minutes)
               reset-idle!        (fn [] (reset! last-activity (js/Date.now)))
               _                  (.addEventListener js/document "mousedown" reset-idle!)
               _                  (.addEventListener js/document "keypress" reset-idle!)
               idle-interval      (js/setInterval
                                    (fn []
                                      (when (and @idle-minutes-ref
                                                 (some? @handle)
                                                 (>= (/ (- (js/Date.now) @last-activity) ms-per-minute)
                                                     @idle-minutes-ref))
                                        (reset! last-activity (js/Date.now))
                                        (reset! paused-by-idle? true)
                                        (reset! stopped-by-button? true)
                                        (on-stop @local-elapsed)))
                                    ms-per-minute)
               stop!              (fn [] (when-let [h @handle] (js/clearInterval h) (reset! handle nil)))]
    (let [is-running?   (some? @handle)
          current-secs  @local-elapsed
          commit!       (fn []
                          (when (fu/valid-duration? @draft)
                            (let [[h m s] (str/split @draft #":" 3)
                                  new-secs (+ (* seconds-per-hour (js/parseInt h))
                                              (* seconds-per-minute (js/parseInt m))
                                              (js/parseInt s))]
                              (mixpanel/track "Run Form Actions"
                                              {"Action" (if (zero? new-secs) "Timer Reset" "Timer Edited Manually")})
                              (reset! local-elapsed new-secs)
                              (on-change new-secs))))]
      (reset! idle-minutes-ref idle-minutes)
      (cond
        (and running? (not is-running?))
        (do (reset! local-elapsed elapsed)
            (reset! paused-by-idle? false)
            (reset! last-activity (js/Date.now))
            (reset! handle (js/setInterval #(swap! local-elapsed inc) ms-per-second)))

        (and (not running?) is-running?)
        (let [by-button? @stopped-by-button?]
          (reset! stopped-by-button? false)
          (stop!)
          (when-not by-button?
            (on-cancel @local-elapsed))))

      [:<>
       [:input.run-timer-input
        {:class       (cond running?  "run-timer-input--running"
                            @editing? "run-timer-input--editing"
                            :else     "run-timer-input--paused")
         :type        "text"
         :read-only   (or running? (not @editing?))
         :value       (if @editing?
                        @draft
                        (fu/format-duration (fu/parse-duration (if is-running? current-secs elapsed))))
         :on-click    (when-not (or running? @editing?)
                        (fn []
                          (reset! draft (fu/format-duration (fu/parse-duration elapsed)))
                          (reset! editing? true)))
         :on-change   #(reset! draft (.. % -target -value))
         :on-blur     (fn []
                        (when @editing? (commit!))
                        (reset! editing? false))
         :on-key-down (fn [e]
                        (case (.-key e)
                          "Enter"  (when (fu/valid-duration? @draft) (.blur (.-target e)))
                          "Escape" (reset! editing? false)
                          nil))}]
       [tooltip/tooltip
        {:placement "top"}
        (fn [hover-props]
          (if running?
            [:button.run-timer-btn.run-timer-btn--stop
             (merge hover-props
                    {:on-click (fn []
                                 (mixpanel/track "Run Form Actions" {"Action" "Timer Stopped"})
                                 (reset! stopped-by-button? true)
                                 (on-stop @local-elapsed))})
             [icons/mux-stop-icon]]
            [:button.run-timer-btn
             (merge hover-props
                    {:on-click (fn []
                                 (mixpanel/track "Run Form Actions" {"Action" "Timer Started"})
                                 (on-start))})
             [icons/mux-start-icon]]))
        (if running?
          (get-translation "run.timer.pause" "Pause timer")
          (get-translation "run.timer.start" "Start timer"))]
       (when @paused-by-idle?
         [:span.run-timer-idle-note
          (get-translation "run.timer.paused-idle" "Timer paused due to inactivity")])])
    (finally
      (stop!)
      (js/clearInterval idle-interval)
      (.removeEventListener js/document "mousedown" reset-idle!)
      (.removeEventListener js/document "keypress" reset-idle!)
      (when (pos? @local-elapsed)
        (on-cancel @local-elapsed)))))

(defn run-timer []
  (fn []
    (let [enabled?      @(re-frame/subscribe [::run-subs/time-management-enabled?])
          elapsed       @(re-frame/subscribe [::run-subs/elapsed])
          running?      @(re-frame/subscribe [::run-subs/running?])
          idle-minutes  @(re-frame/subscribe [::run-subs/timer-idle-minutes])]
      (when enabled?
        [:div.run-timer
         [:span.run-timer-clock [icons/clock]]
         [timer
          {:running?      running?
           :elapsed       elapsed
           :idle-minutes  idle-minutes
           :on-start      (fn []
                            (re-frame/dispatch [::run-events/start-timer])
                            (re-frame/dispatch [::run-events/save-duration {:paused? false}]))
           :on-stop       (fn [current-elapsed]
                            (re-frame/dispatch [::run-events/set-elapsed current-elapsed])
                            (re-frame/dispatch [::run-events/stop-timer])
                            (re-frame/dispatch [::run-events/save-duration {:paused? true :elapsed current-elapsed}]))
           :on-cancel     (fn [current-elapsed]
                            (re-frame/dispatch [::run-events/save-duration {:elapsed current-elapsed}]))
           :on-change     (fn [new-elapsed]
                            (re-frame/dispatch [::run-events/set-elapsed new-elapsed])
                            (re-frame/dispatch [::run-events/save-duration {:paused? true :elapsed new-elapsed}]))}]]))))

(defn run-footer [{:keys [entity-id edit-permissions? edit-locked?]} _standard-save-form!]
  (let [{:keys [next_instances] :as navigation} @(re-frame/subscribe [::run-subs/navigation-data])
        parent-testset @(re-frame/subscribe [::run-subs/parent-testset])
        next-inst      (first next_instances)
        last-test?     (and (contains? navigation :next_instances) (empty? next_instances))
        dirty?         @(re-frame/subscribe [::run-subs/steps-dirty?])
        can-sync?      @(re-frame/subscribe [::run-subs/can-sync?])]
    [:div.run-footer-actions
     [run-timer]
     [buttons/button
      {:disabled? (or (not edit-permissions?) edit-locked? (not can-sync?))
       :primary?  true
       :tooltip   (cond
                    edit-locked?
                    (get-translation "run.edit-locked" "Locked due to test workflow")
                    (not edit-permissions?)
                    (get-translation "run.no-edit-permission" "Disabled due to Permissions Settings")
                    (and dirty? (not can-sync?))
                    (get-translation "run.steps.update-original-test.called-only"
                                     "All edited steps are called test steps"))
       :text      (get-translation "run.steps.sync-to-test" "Update Original Test")
       :on-click  (fn []
                    (mixpanel/track "Run Form Actions" {"Action" "Sync to Test Clicked"})
                    (re-frame/dispatch [::run-events/flush-then-sync entity-id]))}]
     (if (and last-test? parent-testset)
       [buttons/button
        {:primary? true
         :text     (get-translation "run.nav.back-to-testset" "Back to Test Set")
         :on-click (fn []
                     (mixpanel/track "Run Form Actions" {"Action" "Back to Test Set"})
                     (re-frame/dispatch [::run-events/safe-navigate-to-route
                                         :practitest.frontend.testset2/edit
                                         {:testset-id (:id parent-testset)}]))}]
       [buttons/button
        {:primary?  true
         :disabled? (or (nil? next-inst) (not edit-permissions?) edit-locked?)
         :tooltip   (cond
                      edit-locked?
                      (get-translation "run.edit-locked" "Locked due to test workflow")
                      (not edit-permissions?)
                      (get-translation "run.no-edit-permission" "Disabled due to Permissions Settings"))
         :text      (get-translation "run.nav.run-next-test" "Run Next Test")
         :on-click  (fn []
                      (mixpanel/track "Run Form Actions" {"Action" "Run Next Test"})
                      (re-frame/dispatch [::run-events/safe-navigate-to-instance
                                          (:id next-inst)
                                          (:testset_id next-inst)]))}])]))
